<template>
  <div>

    <form
      v-if="isLoadedNormal && rush !== null"
      @submit.prevent="updateRushWithConfirmation"
    >
      <div class="form-group">
        <label for="player-group-selector">Segment</label>
        <b-form-select
          id="player-group-selector"
          v-model="rush.playerGroupId"
          :options="playerGroupOptions"
        />
      </div>

      <hr>

      <b-form-group
        label="Client min version"
        label-for="client-min-version"
        label-class="mb-1"
      >
        <input id="client-min-version" class="form-control" v-model="clientMinVersionStr" required>
      </b-form-group>

      <hr>

      <div class="mb-4">
        <h5>Start</h5>

        <b-form-datepicker
          v-model="plannedStartDateStr"
          :min="minPlannedStartDateStr"
          :start-weekday="MONDAY"
          :readonly="isStimeReadonly"
          class="mb-2"
        />

        <time-input
          v-model="plannedStartTimeStr"
          :readonly="isStimeReadonly"
        />
      </div>

      <div class="mb-4">
        <h5>Finish</h5>

        <b-form-datepicker
          v-model="plannedFinishDateStr"
          :min="minPlannedFinishDateStr"
          :start-weekday="MONDAY"
          :readonly="isFtimeReadonly"
          class="mb-2"
        />

        <time-input
          v-model="plannedFinishTimeStr"
          :readonly="isFtimeReadonly"
        />
      </div>

      <div class="mb-3">
        <h5>Duration</h5>
        <p>
          {{durationStr}}
        </p>
      </div>

      <hr>

      <b-button :to="{name: 'rush_info', params: {rushId: rushId}}">Cancel</b-button>

      <b-button
        type="submit"
        :disabled="isReadonly"
        variant="primary"
      >
        Save
      </b-button>

    </form>

    <p v-if="message !== null">
      {{message}}
    </p>

    <loading-indicator
      v-if="isLoading"
    />

  </div>
</template>

<script>
import { HTTP } from '../http-common.js';
import notifier from '../notifier.js';

import { getPlayerGroupOptionsVerbose } from '../model/event.js';

/**
 * @typedef { import('../model/rush.js').Rush } Rush
 */

import { isRushEditable           } from '../model/rush.js';
import { isRushStimeEditable      } from '../model/rush.js';
import { isRushFtimeEditable      } from '../model/rush.js';

import { confirmAction                       } from '../service/confirm.js';
import { convertStampToDateStr               } from '../util.js';
import { convertStampToTimeStr               } from '../util.js';
import { convertMomentToDateStr              } from '../util.js';
import { getDurationStrBetweenMoments        } from '../util.js';
import { convertDataStrAndTimeStrToLocalData } from '../util.js';
import { convertVersionCodeToStr             } from '../util.js';

import LoadingIndicator from './LoadingIndicator.vue';
import TimeInput        from './TimeInput.vue';

const LOADING_STATUS__INIT    = 1;
const LOADING_STATUS__LOADING = 2;
const LOADING_STATUS__NORMAL  = 3;
const LOADING_STATUS__ERROR   = 4;

export default {
  name: 'RushEdit',

  components: {
    LoadingIndicator,
    TimeInput,
  },

  props: {
    rushId: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      MONDAY: 1,

      loadingStatus: LOADING_STATUS__INIT,

      /** @type {?Rush} */
      rush: null,

      playerGroupOptions: getPlayerGroupOptionsVerbose(),

      plannedStartDateStr: '',
      minPlannedStartDateStr: '',
      plannedStartTimeStr: '',

      plannedFinishDateStr: '',
      minPlannedFinishDateStr: '',
      plannedFinishTimeStr: '',

      clientMinVersionStr: '',
    };
  },

  computed: {
    /** @returns {boolean} */
    isLoading() {
      return this.loadingStatus === LOADING_STATUS__LOADING;
    },

    /** @returns {boolean} */
    isLoadedNormal() {
      return this.loadingStatus === LOADING_STATUS__NORMAL;
    },

    /** @returns {?string} */
    message() {
      if(this.isLoadedNormal && this.rush === null) {
        return `Rush with id ${this.rushId} does not exist.`;
      }
      return null;
    },

    /** @returns {?Date} */
    plannedStartMoment() {
      return convertDataStrAndTimeStrToLocalData(this.plannedStartDateStr, this.plannedStartTimeStr);
    },

    /** @returns {?Date} */
    plannedFinishMoment() {
      return convertDataStrAndTimeStrToLocalData(this.plannedFinishDateStr, this.plannedFinishTimeStr);
    },

    /** @returns {?string} */
    durationStr() {
      const plannedStartMoment = this.plannedStartMoment;
      if(plannedStartMoment === null) {
        return null;
      }

      const plannedFinishMoment = this.plannedFinishMoment;
      if(plannedFinishMoment === null) {
        return null;
      }

      return getDurationStrBetweenMoments(plannedStartMoment, plannedFinishMoment);
    },

    /** @returns {boolean} */
    isReadonly() {
      const rush = this.rush;
      if(rush === null) {
        return true;
      }

      const isEditable = isRushEditable(rush.status);
      return !isEditable;
    },

    /** @returns {boolean} */
    isStimeEditable() {
      const rush = this.rush;
      if(rush === null) {
        return false; // data not loaded
      }

      return isRushStimeEditable(rush.status);
    },

    /** @returns {boolean} */
    isStimeReadonly() {
      return !this.isStimeEditable;
    },

    /** @returns {boolean} */
    isFtimeEditable() {
      const rush = this.rush;
      if(rush === null) {
        return false;
      }

      return isRushFtimeEditable(rush.status);
    },

    /** @returns {boolean} */
    isFtimeReadonly() {
      return !this.isFtimeEditable;
    },
  },

  created() {
    this.loadRush();
  },

  methods: {
    loadRush() {
      notifier.deleteSenderNotifications(this);

      this.loadingStatus = LOADING_STATUS__LOADING;

      const url = `/rush/${this.rushId}/info`;
      HTTP.get(url)
      .then(response => {
        const rush = response.data.data;
        this.rush = rush;

        const now = new Date();
        const nowDateStr = convertMomentToDateStr(now);

        this.plannedStartDateStr     = convertStampToDateStr(rush.stime);
        this.minPlannedStartDateStr  = nowDateStr;
        this.plannedStartTimeStr     = convertStampToTimeStr(rush.stime)

        this.plannedFinishDateStr    = convertStampToDateStr(rush.ftime);
        this.minPlannedFinishDateStr = nowDateStr;
        this.plannedFinishTimeStr    = convertStampToTimeStr(rush.ftime);

        this.clientMinVersionStr     = convertVersionCodeToStr(rush.clientMinVersionCode);

        this.loadingStatus = LOADING_STATUS__NORMAL;
      })
      .catch(err => {
        this.loadingStatus = LOADING_STATUS__ERROR;
        notifier.showError(err, {sender: this});
      });
    },

    updateRushWithConfirmation() {
      this.confirmRushUpdate(() => {
        this.updateRush();
      });
    },

    // function onConfirmed()
    confirmRushUpdate(onConfirmed) {
      confirmAction(
        this,
        'Are you sure you want to save changes for this rush {env}?',
        'Save',
        onConfirmed,
      );
    },

    updateRush() {
      notifier.deleteSenderNotifications(this);

      const plannedStartSec  = Math.floor(this.plannedStartMoment.getTime() / 1000);
      const plannedFinishSec = Math.floor(this.plannedFinishMoment.getTime() / 1000);

      const data = new FormData();
      data.set('stime', plannedStartSec);
      data.set('ftime', plannedFinishSec);
      data.set('player_group_id', this.rush.playerGroupId);

      const trimmedClientMinVersion = this.clientMinVersionStr.trim();
      if(trimmedClientMinVersion.length === 0) {
        notifier.showError('Client min version must be specified.', {sender: this});
        return;
      }

      data.set('client_min_version', trimmedClientMinVersion);

      const url = `/rush/${this.rushId}`;
      HTTP.post(url, data)
      .then(() => {
        notifier.showSuccessAfterTransition('Changes saved.');
        this.$router.push({name: 'rush_info', params: {rushId: this.rushId}});
      })
      .catch(err => {
        notifier.showError(err, {sender: this});
      });
    },
  },
};
</script>
